import { forwardRef, useRef, useImperativeHandle, useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/styles';
import Label from '../Label';
import FieldError from '../FieldError';
import cn from 'clsx';
import maxLengthValidator from '../helpers/maxLengthValidator';

type TextAreaProps = JSX.IntrinsicElements['textarea'] & {
    label?: string;
    required?: boolean;
    error?: string;
    resizable?: boolean;
    minRows?: number;
    maxRows?: number;
    labelClassName?: string;
};

const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
    (
        {
            className,
            label,
            required,
            error,
            resizable = false,
            onChange = () => {},
            maxLength,
            minRows = 0,
            maxRows,
            labelClassName,
            ...domProps
        },
        ref
    ) => {
        const classes = useStyles();
        const classNames = cn(classes.input, className, resizable && 'resizable');
        const textAreaRef = useRef<HTMLTextAreaElement>(null);
        const [charCounter, setCharCounter] = useState<number>(0);
        const [textareaRows, setTextareaRows] = useState<number>(minRows);

        useImperativeHandle(ref, () => textAreaRef.current as HTMLTextAreaElement, []);

        useEffect(() => {
            const initialCounterState = textAreaRef.current?.value?.length ?? 0;
            setCharCounter(initialCounterState);
        }, []);

        const onChangeHandle = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
            if (minRows && maxRows) {
                event.target.rows = minRows;
                const newRows = calcTextAreaHeight({ event, minRows, maxRows });
                if (newRows === maxRows) {
                    event.target.scrollTop = event.target.scrollHeight;
                }
                event.target.rows = newRows;
                setTextareaRows(newRows);
            }
            onChange(event);
        };

        return (
            <label className={classes.container}>
                <textarea
                    ref={textAreaRef}
                    className={classNames}
                    maxLength={maxLength}
                    onChange={maxLengthValidator(e => {
                        setCharCounter(e.target.value?.length);
                        onChangeHandle(e);
                    })}
                    rows={textareaRows}
                    {...domProps}
                />
                <div className={classes.meta}>
                    {label && (
                        <Label className={labelClassName} label={label} required={required} />
                    )}
                    {typeof maxLength === 'number' && (
                        <span
                            className={classes.charCounter}
                        >{`(${charCounter}/${maxLength})`}</span>
                    )}
                    {error && <FieldError error={error} />}
                </div>
            </label>
        );
    }
);

const LINE_HEIGHT = 24;
const calcTextAreaHeight = ({
    event,
    minRows,
    maxRows,
}: {
    event: React.ChangeEvent<HTMLTextAreaElement>;
    minRows: number;
    maxRows: number;
}) => {
    const currentRows = Math.floor(event.target.scrollHeight / LINE_HEIGHT);

    return Math.min(Math.max(minRows, currentRows), maxRows);
};

TextArea.displayName = 'TextArea';

const useStyles = makeStyles(
    theme => ({
        container: {
            display: 'flex',
            flexDirection: 'column-reverse',
        },
        input: {
            color: theme.palette.greys[600],
            padding: '4px 8px',
            overflow: 'auto',
            resize: 'none',
            border: 'none',
            ...theme.typography.t61,
            backgroundColor: theme.palette.greys[200],
            transition: 'color 300ms ease-in-out',
            width: '100%',
            minHeight: 80,
            borderRadius: 6,
            margin: 0,

            '&:disabled': {
                color: theme.palette.greys[400],
                cursor: 'not-allowed',
            },

            '&:focus': {
                outline: 'none',
                boxShadow: `0 0 0 1px ${theme.palette.greys[400]}`,
            },

            '&.resizable': {
                resize: 'both',
            },
        },
        meta: {
            display: 'flex',
            alignItems: 'center',
            marginBottom: 4,
        },
        charCounter: {
            ...theme.typography.t81,
            color: theme.palette.greys[500],
            lineHeight: '18px',
            marginLeft: 4,
            fontVariantNumeric: 'tabular-nums',
        },
    }),
    { classNamePrefix: 'textarea' }
);

export default TextArea;
